import { Checkbox, type CheckboxProps, FormHelperText } from "@mui/material";
import Box from "@mui/material/Box";
import FormControl, { type FormControlProps } from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import type { TFunction } from "core/entities";
import type {
  EstimateDamageLocation,
  EstimateRequestDamageInformationFormValues,
  EstimateRequestDamageLocationFormValue,
} from "features/EstimateRequest/entities";
import { useEstimateRequestEnumTranslations } from "features/EstimateRequest/hooks/useEstimateRequestEnumTranslations";
import { type UseFormReturn, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CarSVG } from "./CarSVG";

export function DamageLocationCheckboxGroup({
  formMethods,
  t,
  formControlProps,
}: {
  formMethods: UseFormReturn<EstimateRequestDamageInformationFormValues>;
  t: TFunction;
  formControlProps?: FormControlProps;
}) {
  const { t: tCore } = useTranslation("Fixhub");
  const { fieldState, field } = useController({
    name: "damageLocations",
    control: formMethods.control,
    rules: {
      validate: (value) => {
        if (!Array.isArray(value)) {
          return tCore("Next:Core:FieldRequiredValidationMessage");
        }
        if (value.length < 1) {
          return tCore("Next:Core:FieldRequiredValidationMessage");
        }
        return true;
      },
    },
  });

  const { error } = fieldState;
  const errorMessage = error?.message;
  const enumTranslations = useEstimateRequestEnumTranslations();

  function isValueChecked(value: EstimateDamageLocation) {
    return field.value.some(
      (damageLocation) => damageLocation.location === value
    );
  }
  function getOnChange(
    value: EstimateDamageLocation
  ): CheckboxProps["onChange"] {
    return (_, checked) => {
      const currentDamageLocations = formMethods.getValues("damageLocations");
      if (checked) {
        const newValue: EstimateRequestDamageLocationFormValue = {
          location: value,
          images: [
            { file: null, fileName: null, fileId: null },
            { file: null, fileName: null, fileId: null },
          ],
        };
        field.onChange([...currentDamageLocations, newValue]);
      } else {
        field.onChange(
          currentDamageLocations.filter((v) => v.location !== value)
        );
      }
    };
  }

  return (
    <FormControl
      component="fieldset"
      error={Boolean(errorMessage)}
      {...formControlProps}
    >
      <FormLabel component="legend">{t("damageLocationLabel")}</FormLabel>
      {Boolean(errorMessage) && (
        <FormHelperText sx={{ color: "error.main" }}>
          {errorMessage}
        </FormHelperText>
      )}
      <Box
        position="relative"
        marginBlockStart={2}
        width="fit-content"
        marginInline="auto"
      >
        <Checkbox
          sx={{
            position: "absolute",
            top: "6%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("FrontCenter")}
          onChange={getOnChange("FrontCenter")}
          aria-label={enumTranslations.EstimateImageLocation.FrontCenter}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "15%",
            left: "15%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("FrontLeft")}
          onChange={getOnChange("FrontLeft")}
          aria-label={enumTranslations.EstimateImageLocation.FrontLeft}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "15%",
            left: "85%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("FrontRight")}
          onChange={getOnChange("FrontRight")}
          aria-label={enumTranslations.EstimateImageLocation.FrontRight}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "50%",
            left: "12%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("SideLeft")}
          onChange={getOnChange("SideLeft")}
          aria-label={enumTranslations.EstimateImageLocation.SideLeft}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "50%",
            left: "88%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("SideRight")}
          onChange={getOnChange("SideRight")}
          aria-label={enumTranslations.EstimateImageLocation.SideRight}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "85%",
            left: "15%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("BackLeft")}
          onChange={getOnChange("BackLeft")}
          aria-label={enumTranslations.EstimateImageLocation.BackLeft}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "85%",
            left: "85%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("BackRight")}
          onChange={getOnChange("BackRight")}
          aria-label={enumTranslations.EstimateImageLocation.BackRight}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "93%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("BackCenter")}
          onChange={getOnChange("BackCenter")}
          aria-label={enumTranslations.EstimateImageLocation.BackCenter}
        />
        <Checkbox
          sx={{
            position: "absolute",
            top: "55%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          checked={isValueChecked("Top")}
          onChange={getOnChange("Top")}
          aria-label={enumTranslations.EstimateImageLocation.Top}
        />
        <CarSVG height="100%" lineStroke="hsl(0,0%,65%)" />
      </Box>
    </FormControl>
  );
}
